import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const HOST = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3003'
  : 'https://api.blockhivetechnologies.com';

const FLAG = { USD: '🇺🇸', GBP: '🇬🇧', CNY: '🇨🇳' };

const EVENT_DESCRIPTIONS = [
  [/nonfarm|non.farm|payroll/i,              `Monthly count of US jobs added outside agriculture. The Fed's primary labor market signal — beats push rate-hike expectations higher, misses open the door to pivots. Consistently the highest-volatility release of the month.`],
  [/\bcpi\b|consumer price index/i,          `Measures change in prices paid by consumers. The headline inflation gauge — hot prints pressure the Fed to hold or hike rates, directly weighing on risk assets. Shelter, energy, and core readings drive the reaction.`],
  [/\bppi\b|producer price/i,                `Tracks prices at the wholesale level before they reach consumers. Leads CPI by weeks — when producer input costs rise, consumer prices follow. A hot read shifts rate-hike bets before the CPI even drops.`],
  [/\bpce\b|personal consumption expenditure/i, `The Fed's preferred inflation measure, embedded in every FOMC decision. Core PCE strips food and energy; sustained readings above 2% are the clearest trigger for rate-hike repricing.`],
  [/fomc minutes/i,                          `Detailed transcript of the last Fed meeting. Markets parse language shifts for policy clues — "hawkish" tone raises hike bets, "dovish" tone fuels rally expectations across risk assets.`],
  [/fomc|federal open market|rate decision|interest rate/i, `Federal Reserve rate decision and policy statement. The single most market-moving event — hikes strengthen the dollar and compress risk assets; cuts do the opposite. The press conference Q&A often moves markets more than the decision itself.`],
  [/powell|fed.*speak|fed.*chair|fed.*speech|fed.*testif|yellen|waller|jefferson/i, `Fed official speech or testimony. Any deviation from the established tone — more hawkish or more dovish — instantly reprices rate expectations and moves crypto, equities, and bonds simultaneously.`],
  [/\bgdp\b|gross domestic/i,                `Measures total economic output for the quarter. Strong GDP signals the economy is running hot and the Fed stays hawkish; contraction readings raise recession risk and pivot expectations. Often revised significantly.`],
  [/ism.*manufact|manufact.*ism/i,           `Monthly survey of manufacturing purchasing managers on business conditions. Above 50 = expansion, below 50 = contraction. Leading indicator of industrial activity and supply-chain price pressure.`],
  [/ism.*serv|serv.*ism/i,                   `Survey of the US service sector, which drives 80% of the economy. The prices-paid sub-index is closely watched for inflation signals — readings above 60 historically trigger hawkish Fed rhetoric.`],
  [/\bpmi\b|purchasing managers/i,           `Monthly survey of purchasing managers on current business conditions. Flash estimates released mid-month move markets early; sustained sub-50 readings signal economic contraction ahead.`],
  [/retail sales/i,                          `Monthly snapshot of consumer spending at stores and online. Consumption drives 70% of US GDP — strong prints reinforce hawkish Fed positioning; weak prints raise slowdown fears.`],
  [/initial.*claim|jobless.*claim|unemployment.*claim|weekly.*claim/i, `Weekly count of new unemployment filings — the most real-time labor data available. A sustained rise signals labor market softening and builds expectations for a Fed pivot toward rate cuts.`],
  [/\badp\b.*employ|employ.*\badp\b/i,       `Private-sector jobs estimate released two days before NFP. Provides an early read on labor momentum; large divergences from consensus frequently front-run NFP-day volatility.`],
  [/jolts|job openings/i,                    `Monthly count of US job vacancies. High openings signal tight labor demand and wage pressure that keeps inflation elevated. Fed officials cite JOLTS directly when assessing labor market tightness.`],
  [/durable goods/i,                         `Orders for long-lasting manufactured items — planes, machines, electronics. A proxy for business investment confidence; strong orders signal firms expect sustained growth, sustaining inflation pressure.`],
  [/consumer confidence/i,                   `Survey of how confident households feel about current and future economic conditions. A leading demand indicator — sharp drops often precede spending slowdowns by 1–2 months.`],
  [/consumer sentiment|michigan/i,           `University of Michigan survey measuring consumer attitudes and inflation expectations. The 1-year and 5-year inflation expectations sub-indexes are watched by the Fed as anchors for policy credibility.`],
  [/housing starts|building permits/i,       `Measures new residential construction activity. One of the most interest-rate-sensitive sectors — rapid declines signal Fed tightening is working and the economy is cooling.`],
  [/home sales|existing home|new home/i,     `Volume of residential property transactions. Sustained declines reflect higher mortgage rates filtering into the real economy; bellwether for how much rate policy is biting consumer demand.`],
  [/trade balance|current account/i,         `Difference between US exports and imports. Persistent deficits can weigh on the USD over time; large swings affect dollar strength, which inversely correlates with Bitcoin on major moves.`],
  [/industrial production|capacity utilization/i, `Factory, mining, and utility output. Running above capacity is inflationary; below-trend output signals economic slack that gives the Fed room to cut without triggering price spikes.`],
  [/unemployment rate/i,                     `Monthly percentage of the labor force actively seeking work. Fed's dual mandate includes maximum employment — a rising unemployment rate is the clearest signal a pivot from hikes to cuts is approaching.`],
  [/\bboe\b|bank of england|\bmpc\b/i,       `Bank of England rate decision. UK policy shifts affect GBP and European risk sentiment; unexpected changes can spread risk-off or risk-on dynamics to crypto through global capital flow rebalancing.`],
  [/pboc|bank of china|china.*rate|loan prime/i, `People's Bank of China monetary policy action. As the world's second-largest economy, unexpected Chinese liquidity injections or cuts move global commodity prices and risk appetite.`],
  [/\becb\b|european central bank/i,         `European Central Bank rate decision. EUR movements triggered by ECB shifts affect global capital flows and dollar strength, which inversely influences Bitcoin on major deviations.`],
  [/china.*cpi|china.*ppi|china.*inflation/i, `China's inflation data. Deflation risk in China signals weak domestic demand, pressuring commodity prices and global risk sentiment through supply-chain and trade channels.`],
  [/china.*gdp|china.*growth/i,              `China's quarterly economic output. As a top trading partner for most nations, a miss signals global slowdown risk; beats lift commodity demand and global risk appetite.`],
  [/caixin|china.*pmi/i,                     `Caixin PMI surveys China's private-sector firms, often diverging from the official government PMI. Surprise moves shift global growth expectations and commodity demand outlook.`],
  [/uk.*cpi|uk.*inflation|britain.*cpi/i,    `UK consumer inflation reading. BOE watches this closely — persistently high UK CPI increases GBP volatility and can spread risk-off sentiment across European markets.`],
  [/uk.*gdp|britain.*gdp/i,                  `UK economic output. Signals whether BOE rate hikes are tipping the economy toward recession; contraction prints are GBP-negative and often spread broader risk-off pressure.`],
  [/uk.*employ|uk.*jobs|claimant/i,          `UK labor market data. The wage growth sub-index is the key inflation input for BOE decisions — strong wages keep rates higher, weak employment points toward cuts.`],
];

function getEventDesc(title) {
  for (const [re, desc] of EVENT_DESCRIPTIONS) {
    if (re.test(title)) return desc;
  }
  return null;
}

const CITIES = [
  { label: 'NY',  tz: 'America/New_York' },
  { label: 'LON', tz: 'Europe/London' },
  { label: 'CNY', tz: 'Asia/Shanghai' },
  { label: 'SYD', tz: 'Australia/Sydney' },
  { label: 'SF',  tz: 'America/Los_Angeles' },
];

function cityTime(tz, date) {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz });
}

// Parse eco values like "2.3%", "15.2K", "-0.3" into a float
function parseEcoVal(v) {
  if (!v && v !== 0) return null;
  const s = String(v).replace(/[%,\s]/g, '');
  const mult = s.toUpperCase().endsWith('K') ? 1e3 : s.toUpperCase().endsWith('M') ? 1e6 : s.toUpperCase().endsWith('B') ? 1e9 : 1;
  const n = parseFloat(s) * mult;
  return isNaN(n) ? null : n;
}

// Strip measurement suffixes/prefixes to get base event name for deduplication
function baseName(title) {
  return title
    .replace(/^(Core\s+|Flash\s+|Prelim\s+)/i, '')
    .replace(/\s+(m\/m|y\/y|q\/q|mom|yoy|qoq|final|prelim|flash|revised|nsa|sa|index)$/i, '')
    .trim();
}

// Deduplicate events on the same day with the same base name — keep earliest time
function dedupeDay(events) {
  const seen = new Map();
  for (const e of events) {
    const key = baseName(e.title).toLowerCase();
    if (!seen.has(key)) {
      seen.set(key, { ...e, _base: baseName(e.title) });
    }
    // keep earliest time (events already sorted ascending)
  }
  return Array.from(seen.values());
}

function fmtDay(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric', timeZone: 'UTC' });
}

function fmtTime(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' });
}

function fmtCountdown(dateStr, now) {
  const diff = new Date(dateStr) - now;
  if (diff <= 0) return null;
  const totalMin = Math.floor(diff / 60000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function groupByDay(events) {
  const groups = [];
  let current = null;
  for (const e of events) {
    const day = new Date(e.date).toISOString().slice(0, 10);
    if (!current || current.day !== day) {
      current = { day, label: fmtDay(e.date), events: [] };
      groups.push(current);
    }
    current.events.push(e);
  }
  // dedupe within each day
  groups.forEach(g => { g.events = dedupeDay(g.events); });
  return groups;
}

export default function EcoCalendar() {
  const [events, setEvents] = useState([]);
  const [now, setNow] = useState(new Date());
  const [error, setError] = useState(null);
  const eventsRef = useRef(null);
  const nextRef   = useRef(null);
  const ttRef     = useRef(null);

  const showTip = (text, ev) => {
    const el = ttRef.current; if (!el) return;
    el.textContent = text;
    const x = ev.clientX + 14, y = ev.clientY + 14;
    el.style.left = (x + el.offsetWidth > window.innerWidth - 8 ? ev.clientX - el.offsetWidth - 14 : x) + 'px';
    el.style.top  = (y + el.offsetHeight > window.innerHeight - 8 ? ev.clientY - el.offsetHeight - 14 : y) + 'px';
    el.style.display = 'block';
  };
  const moveTip = (ev) => {
    const el = ttRef.current; if (!el || el.style.display === 'none') return;
    const x = ev.clientX + 14, y = ev.clientY + 14;
    el.style.left = (x + el.offsetWidth > window.innerWidth - 8 ? ev.clientX - el.offsetWidth - 14 : x) + 'px';
    el.style.top  = (y + el.offsetHeight > window.innerHeight - 8 ? ev.clientY - el.offsetHeight - 14 : y) + 'px';
  };
  const hideTip = () => { if (ttRef.current) ttRef.current.style.display = 'none'; };

  const load = async () => {
    try {
      const res = await axios.get(`${HOST}/eco-calendar`, { timeout: 10000 });
      setEvents(res.data || []);
      setError(null);
    } catch (e) {
      setError('Unavailable');
    }
  };

  useEffect(() => {
    load();
    const dataTimer = setInterval(load, 4 * 60 * 60 * 1000);
    const clockTimer = setInterval(() => setNow(new Date()), 30000);
    return () => { clearInterval(dataTimer); clearInterval(clockTimer); };
  }, []);

  const groups = groupByDay(events);

  // Find next upcoming event for highlighting
  const nextEvent = events.find(e => new Date(e.date) > now);

  // Scroll the next event into view inside the scrollable container — runs once on data load
  const didScrollRef = useRef(false);
  useEffect(() => {
    if (didScrollRef.current) return;
    if (!nextRef.current || !eventsRef.current) return;
    const container = eventsRef.current;
    const target = nextRef.current;
    // Offset target so it appears near the top of the visible area
    container.scrollTop = Math.max(0, target.offsetTop - container.offsetTop - 4);
    didScrollRef.current = true;
  }, [events]);

  return (
    <div className="eco-calendar">
      <div className="eco-header">
        <span className="eco-title">ECO CALENDAR</span>
        <span className="eco-sub">
          {error ? <span className="eco-error">{error}</span> : !events.length ? '...' : `${events.length} events`}
        </span>
      </div>

      {(() => {
        const items = CITIES.map(c => `${c.label} ${cityTime(c.tz, now)}`).join('  ·  ');
        return (
          <div className="eco-clock-track">
            <div className="eco-clock-inner">
              {items}  ·  {items}  ·&nbsp;
            </div>
          </div>
        );
      })()}

      <div className="eco-col-headers">
        <span>TIME</span>
        <span></span>
        <span>EVENT</span>
        <span>PREV</span>
        <span>DIR</span>
        <span>IN</span>
        <span></span>
      </div>

      <div className="eco-events" ref={eventsRef}>
        {groups.length === 0 && !error && (
          <div className="eco-empty">
            {events.length === 0 ? 'Loading...' : 'No upcoming events — next week calendar publishes Sunday'}
          </div>
        )}

        {groups.map(group => (
          <div key={group.day} className="eco-day-group">
            <div className="eco-day-label">{group.label}</div>
            {group.events.map((e, i) => {
              const countdown = fmtCountdown(e.date, now);
              const isPast = new Date(e.date) <= now;
              const isNext = nextEvent && e.date === nextEvent.date && e.title === nextEvent.title;
              const actualVal  = parseEcoVal(e.actual);
              const prevVal    = parseEcoVal(e.previous);
              const hasActual  = actualVal !== null;
              // If actual released: + if beat previous, − if missed. Else: sign of previous value.
              const dir        = hasActual && prevVal !== null
                ? (actualVal > prevVal ? '+' : actualVal < prevVal ? '−' : '·')
                : prevVal !== null
                  ? (prevVal > 0 ? '+' : prevVal < 0 ? '−' : '·')
                  : null;
              const dirColor   = dir === '+' ? '#00ff88' : dir === '−' ? '#ff4444' : '#444';
              const desc = getEventDesc(e.title);
              return (
                <div key={i} ref={isNext ? nextRef : null}
                  className={`eco-row${isPast ? ' eco-past' : ''}${isNext ? ' eco-next' : ''}${desc ? ' eco-row-tip' : ''}`}
                  onMouseEnter={desc ? (ev) => showTip(desc, ev) : undefined}
                  onMouseMove={desc ? moveTip : undefined}
                  onMouseLeave={desc ? hideTip : undefined}
                >
                  <span className="eco-time">{fmtTime(e.date)}</span>
                  <span className="eco-flag">{FLAG[e.country] || e.country}</span>
                  <span className="eco-name">{e._base || e.title}</span>
                  <span className="eco-actual" style={{ color: hasActual ? '#aaa' : '#444' }}>{hasActual ? e.actual : (e.previous || '')}</span>
                  <span className="eco-dir" style={{ color: dir ? dirColor : 'transparent' }}>{dir || '·'}</span>
                  <span className="eco-countdown">{countdown || ''}</span>
                  <span className="eco-dot" />
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {/* Floating event description tooltip — direct DOM updates, no re-render on mousemove */}
      <div ref={ttRef} style={{
        display: 'none', position: 'fixed', zIndex: 9999, pointerEvents: 'none',
        maxWidth: 260, padding: '8px 11px',
        background: 'rgba(6,6,6,0.97)',
        border: '1px solid #1e1e1e',
        borderTop: '1px solid #2e2e2e',
        fontFamily: 'monospace', fontSize: 9, color: '#777',
        letterSpacing: '0.4px', lineHeight: 1.65,
      }} />
    </div>
  );
}
